/**
 * Mordeczka.eu Admin - Movie Form Page
 */

import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Card,
  Row,
  Col,
  Typography,
  Space,
  Spin,
  message,
  Divider,
  Modal,
  Upload,
} from "antd";
import {
  SaveOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
  UploadOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  worksApi,
  languagesApi,
  genresApi,
  directorsApi,
  imagesApi,
  workImagesApi,
  nationalitiesApi,
  type MovieInfoResponse,
} from "../services/api";
import type { WorkCreate, WorkUpdate } from "../types";
import { AIMovieHelper } from "../components/AIMovieHelper";
import { WorkImagesManager } from "../components/WorkImagesManager";
import { usePermissions } from "../hooks/usePermissions";

const { Title } = Typography;
const { TextArea } = Input;

export function MovieFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [directorForm] = Form.useForm();
  const isEditing = Boolean(id);
  const { canCreate, canEdit } = usePermissions();

  const isReadOnly = isEditing ? !canEdit : !canCreate;

  // Modal state
  const [isDirectorModalOpen, setIsDirectorModalOpen] = useState(false);

  // Cover image state
  const [coverImageId, setCoverImageId] = useState<string | null>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(
    null,
  );
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [pendingAdditionalImageUrls, setPendingAdditionalImageUrls] = useState<string[]>([]);

  // Ref for AI Helper callback
  const onDirectorAddedRef = useRef<((directorId: string) => void) | null>(
    null,
  );

  // Search states for remote fetching
  const [directorSearch, setDirectorSearch] = useState("");
  const [debouncedDirectorSearch, setDebouncedDirectorSearch] = useState("");
  const [aiDirectorOption, setAiDirectorOption] = useState<{
    value: string;
    label: string;
  } | null>(null);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedDirectorSearch(directorSearch),
      300,
    );
    return () => clearTimeout(timer);
  }, [directorSearch]);

  // Fetch work for editing
  const { data: work, isLoading: workLoading } = useQuery({
    queryKey: ["work", id],
    queryFn: () => worksApi.get(id!),
    enabled: isEditing,
  });

  // Fetch languages
  const { data: languages } = useQuery({
    queryKey: ["languages-all"],
    queryFn: () => languagesApi.list({ page_size: 1000 }),
  });

  // Fetch genres (for movies)
  const { data: genres } = useQuery({
    queryKey: ["genres-all"],
    queryFn: () => genresApi.list({ page_size: 1000 }),
  });

  // Fetch directors with remote search
  const { data: directorsData, isLoading: directorsLoading } = useQuery({
    queryKey: ["directors-search", debouncedDirectorSearch],
    queryFn: () =>
      directorsApi.list({ search: debouncedDirectorSearch, page_size: 50 }),
  });

  // Merge search results with the currently selected director to ensure it's always visible
  const directorOptions = useMemo(() => {
    const options = (directorsData?.items || []).map((d) => ({
      value: d.id,
      label: d.name,
    }));

    // If we have a current director from the work data, make sure it's in the list
    const currentDirector = work?.movie_details?.director;
    if (
      currentDirector &&
      !options.find((o) => o.value === currentDirector.id)
    ) {
      options.push({
        value: currentDirector.id,
        label: currentDirector.name,
      });
    }
    if (aiDirectorOption && !options.find((o) => o.value === aiDirectorOption.value)) {
      options.push(aiDirectorOption);
    }

    return options;
  }, [directorsData, work, aiDirectorOption]);

  // Fetch nationalities
  const { data: nationalities } = useQuery({
    queryKey: ["nationalities-all"],
    queryFn: () => nationalitiesApi.list({ page_size: 1000 }),
  });

  // Set form values when editing
  useEffect(() => {
    if (work) {
      form.setFieldsValue({
        title: work.title,
        original_title: work.original_title,
        original_language_id: work.original_language_id,
        release_year: work.release_year,
        description: work.description,
        external_url: work.external_url,
        // Movie details
        duration_minutes: work.movie_details?.duration_minutes,
        director_id: work.movie_details?.director_id,
        // Genre IDs
        genre_ids: work.genres?.map((g) => g.id) || [],
        nationality_id: work.movie_details?.nationality_id,
      });
      // Set cover image if exists
      if (work.cover_image_id && work.cover_image) {
        setCoverImageId(work.cover_image_id);
        setCoverImagePreview(imagesApi.getFileUrl(work.cover_image_id));
      }
    }
  }, [work, form]);

  const attachPendingAdditionalImages = async (workId: string, title?: string) => {
    if (pendingAdditionalImageUrls.length === 0) return;

    let failed = 0;
    for (let index = 0; index < pendingAdditionalImageUrls.length; index += 1) {
      const url = pendingAdditionalImageUrls[index];
      try {
        const uploadedImage = await imagesApi.uploadFromUrl(
          url,
          `${title || "Film"} - obrazek ${index + 1}`,
        );
        await workImagesApi.add(workId, {
          image_id: uploadedImage.id,
          position: index,
        });
      } catch (error) {
        console.error("Failed to import additional image:", error);
        failed += 1;
      }
    }

    if (failed > 0) {
      message.warning(`Nie udalo sie dodac ${failed} dodatkowych obrazkow`);
    }
    setPendingAdditionalImageUrls([]);
  };

  const createMutation = useMutation({
    mutationFn: (data: WorkCreate) => worksApi.create(data),
    onSuccess: async (createdWork) => {
      await attachPendingAdditionalImages(createdWork.id, createdWork.title);
      queryClient.invalidateQueries({ queryKey: ["works"] });
      message.success("Film utworzony pomyślnie");
      navigate("/movies");
    },
    onError: () => message.error("Nie udało się utworzyć"),
  });

  const updateMutation = useMutation({
    mutationFn: (data: WorkUpdate) => worksApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["works"] });
      queryClient.invalidateQueries({ queryKey: ["work", id] });
      message.success("Film zaktualizowany pomyślnie");
      navigate("/movies");
    },
    onError: () => message.error("Nie udało się zaktualizować"),
  });

  const createDirectorMutation = useMutation({
    mutationFn: (data: { name: string; nationality_id?: string }) =>
      directorsApi.create(data),
    onSuccess: (newDirector) => {
      queryClient.invalidateQueries({ queryKey: ["directors-all"] });
      form.setFieldValue("director_id", newDirector.id);
      if (onDirectorAddedRef.current) {
        onDirectorAddedRef.current(newDirector.id);
      }
      message.success("Reżyser dodany pomyślnie");
      setIsDirectorModalOpen(false);
      directorForm.resetFields();
    },
    onError: () => message.error("Nie udało się dodać reżysera"),
  });

  const onFinish = (values: Record<string, unknown>) => {
    if (isReadOnly) return;

    const payload: WorkCreate | WorkUpdate = {
      type: "movie",
      title: values.title as string,
      original_title: values.original_title as string | undefined,
      original_language_id: values.original_language_id as string | undefined,
      release_year: values.release_year as number | undefined,
      description: values.description as string | undefined,
      external_url: values.external_url as string | undefined,
      cover_image_id: coverImageId || undefined,
      genre_ids: values.genre_ids as string[] | undefined,
      movie_details: {
        duration_minutes: values.duration_minutes as number | undefined,
        director_id: values.director_id as string | undefined,
        nationality_id: values.nationality_id as string | undefined,
      },
    };

    if (isEditing) {
      updateMutation.mutate(payload as WorkUpdate);
    } else {
      createMutation.mutate(payload as WorkCreate);
    }
  };

  if (isEditing && workLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <Spin size="large" tip="Ładowanie..." />
      </div>
    );
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const findLanguageId = (movieInfo: MovieInfoResponse): string | undefined => {
    const code = movieInfo.original_language_code?.toLowerCase();
    const name = movieInfo.original_language?.toLowerCase();
    const match = languages?.items.find((language) => {
      const languageCode = language.code?.toLowerCase();
      const languageName = language.name?.toLowerCase();
      if (!languageName) return false;
      return (
        (code && languageCode === code) ||
        (name && (languageName === name || languageName.includes(name) || name.includes(languageName)))
      );
    });
    return match?.id;
  };

  const findNationalityId = (movieInfo: MovieInfoResponse): string | undefined => {
    const codes = (movieInfo.production_country_codes || []).map((code) => code.toLowerCase());
    const countries = (movieInfo.production_countries || []).map((country) => country.toLowerCase());
    const match = nationalities?.items.find((nationality) => {
      const nationalityCode = nationality.code?.toLowerCase();
      const nationalityName = nationality.name?.toLowerCase();
      if (!nationalityName) return false;
      return (
        (nationalityCode && codes.includes(nationalityCode)) ||
        countries.some(
          (country) =>
            nationalityName === country ||
            nationalityName.includes(country) ||
            country.includes(nationalityName),
        )
      );
    });
    return match?.id;
  };

  const handleMovieDataAccept = async (
    movieInfo: MovieInfoResponse,
    addedDirectorId?: string,
  ) => {
    const matchedGenreIds = movieInfo.genre_ids || [];
    const finalDirectorId = addedDirectorId || movieInfo.director_id;
    const suggestedDirector = movieInfo.suggested_directors?.[0];
    const languageId = findLanguageId(movieInfo);
    const nationalityId = findNationalityId(movieInfo);
    setPendingAdditionalImageUrls(movieInfo.additional_image_urls || []);

    // Upload cover image from URL if provided
    if (movieInfo.cover_url) {
      try {
        setIsUploadingImage(true);
        const uploadedImage = await imagesApi.uploadFromUrl(
          movieInfo.cover_url,
          movieInfo.title || undefined,
        );
        setCoverImageId(uploadedImage.id);
        setCoverImagePreview(imagesApi.getFileUrl(uploadedImage.id));
        message.success("Okładka pobrana pomyślnie");
      } catch (error) {
        console.error("Failed to upload cover image:", error);
        message.warning("Nie udało się pobrać okładki");
      } finally {
        setIsUploadingImage(false);
      }
    }

    const formValues: Record<string, any> = {
      title: movieInfo.title || undefined,
      original_title: movieInfo.original_title || undefined,
      original_language_id: languageId,
      release_year: movieInfo.release_year || undefined,
      description: movieInfo.description || undefined,
      duration_minutes: movieInfo.duration_minutes || undefined,
      genre_ids: matchedGenreIds,
      nationality_id: nationalityId,
    };

    if (finalDirectorId) {
      formValues.director_id = finalDirectorId;
      if (suggestedDirector) {
        setAiDirectorOption({ value: finalDirectorId, label: suggestedDirector });
      }
    }

    form.setFieldsValue(formValues);
  };

  const handleAddMissingDirector = (directorName: string) => {
    directorForm.setFieldsValue({ name: directorName });
    setIsDirectorModalOpen(true);
  };

  const handleDirectorAdded = (callback: (directorId: string) => void) => {
    onDirectorAddedRef.current = callback;
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          style={{ padding: 0 }}
        >
          Powrót
        </Button>
        <Title level={2} style={{ marginTop: 8, marginBottom: 0 }}>
          {isEditing
            ? isReadOnly
              ? "Podgląd Filmu"
              : "Edytuj Film"
            : "Dodaj Film"}
        </Title>
      </div>

      <Card>
        {/* AI Helper - only for new works */}
        {!isEditing && (
          <AIMovieHelper
            onAccept={handleMovieDataAccept}
            onAddMissingDirector={handleAddMissingDirector}
            onDirectorAdded={handleDirectorAdded}
          />
        )}

        <Form form={form} layout="vertical" onFinish={onFinish}>
          {/* Common Fields */}
          {isEditing && work?.slug && (
            <Form.Item label="Slug (URL)">
              <Typography.Text code copyable>
                {work.slug}
              </Typography.Text>
            </Form.Item>
          )}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="title"
                label="Tytuł"
                rules={[{ required: true, message: "Tytuł jest wymagany" }]}
              >
                <Input placeholder="Wprowadź tytuł" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="original_title" label="Tytuł oryginalny">
                <Input placeholder="Oryginalny tytuł (jeśli inny)" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="release_year" label="Rok premiery">
                <InputNumber
                  min={1800}
                  max={2100}
                  placeholder="e.g. 2024"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="original_language_id" label="Język oryginalny">
                <Select placeholder="Wybierz język" allowClear>
                  {languages?.items.map((lang) => (
                    <Select.Option key={lang.id} value={lang.id}>
                      {lang.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Okładka">
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  {coverImagePreview && (
                    <div
                      style={{ position: "relative", display: "inline-block" }}
                    >
                      <img
                        src={coverImagePreview}
                        alt="Okładka"
                        style={{
                          maxWidth: 150,
                          maxHeight: 200,
                          objectFit: "contain",
                          border: "1px solid #d9d9d9",
                          borderRadius: 4,
                        }}
                      />
                      {!isReadOnly && (
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          size="small"
                          style={{ position: "absolute", top: 4, right: 4 }}
                          onClick={() => {
                            setCoverImageId(null);
                            setCoverImagePreview(null);
                          }}
                        />
                      )}
                    </div>
                  )}
                  {!isReadOnly && (
                    <Upload
                      accept="image/*"
                      showUploadList={false}
                      beforeUpload={async (file) => {
                        try {
                          setIsUploadingImage(true);
                          const uploadedImage = await imagesApi.upload(file);
                          setCoverImageId(uploadedImage.id);
                          setCoverImagePreview(
                            imagesApi.getFileUrl(uploadedImage.id),
                          );
                          message.success("Okładka przesłana pomyślnie");
                        } catch (error) {
                          console.error("Failed to upload image:", error);
                          message.error("Nie udało się przesłać okładki");
                        } finally {
                          setIsUploadingImage(false);
                        }
                        return false;
                      }}
                    >
                      <Button
                        icon={<UploadOutlined />}
                        loading={isUploadingImage}
                      >
                        {coverImagePreview
                          ? "Zmień okładkę"
                          : "Wybierz okładkę"}
                      </Button>
                    </Upload>
                  )}
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="external_url" label="Link do źródła">
            <Input placeholder="np. https://filmweb.pl/film/..." />
          </Form.Item>

          <Form.Item name="description" label="Opis">
            <TextArea rows={4} placeholder="Wprowadź opis..." />
          </Form.Item>

          {/* Movie-specific fields */}
          <Divider>Szczegóły filmu</Divider>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="duration_minutes" label="Czas trwania (minuty)">
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="director_id" label="Reżyser">
                <Select
                  placeholder="Wybierz reżysera"
                  allowClear
                  showSearch
                  onSearch={setDirectorSearch}
                  filterOption={false}
                  loading={directorsLoading}
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      {!isReadOnly && (
                        <>
                          <Divider style={{ margin: "8px 0" }} />
                          <Space style={{ padding: "0 8px 4px" }}>
                            <Button
                              type="text"
                              icon={<PlusOutlined />}
                              onClick={() => setIsDirectorModalOpen(true)}
                              block
                            >
                              Dodaj nowego reżysera
                            </Button>
                          </Space>
                        </>
                      )}
                    </>
                  )}
                >
                  {directorOptions.map((director) => (
                    <Select.Option key={director.value} value={director.value}>
                      {director.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="genre_ids" label="Gatunki filmowe">
                <Select
                  mode="multiple"
                  placeholder="Wybierz gatunki filmowe"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.children)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {genres?.items.map((genre) => (
                    <Select.Option key={genre.id} value={genre.id}>
                      {genre.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="nationality_id" label="Kraj produkcji">
                <Select
                  placeholder="Wybierz kraj"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.children)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {nationalities?.items.map((nationality) => (
                    <Select.Option key={nationality.id} value={nationality.id}>
                      {nationality.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Actions */}
          <Form.Item
            style={{ marginTop: 24, marginBottom: 0, textAlign: "right" }}
          >
            <Space>
              <Button onClick={() => navigate(-1)}>
                {isReadOnly ? "Wróć" : "Anuluj"}
              </Button>
              {!isReadOnly && (
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={isSubmitting}
                >
                  {isEditing ? "Zapisz zmiany" : "Dodaj"}
                </Button>
              )}
              {isReadOnly && (
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  disabled
                  title="Brak uprawnień do edycji"
                >
                  {isEditing ? "Zapisz zmiany" : "Dodaj"}
                </Button>
              )}
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* Additional Images - only in edit mode */}
      {isEditing && id && (
        <WorkImagesManager workId={id} readOnly={isReadOnly} />
      )}

      {/* Modal for adding new director */}
      <Modal
        title="Dodaj nowego reżysera"
        open={isDirectorModalOpen}
        onCancel={() => {
          setIsDirectorModalOpen(false);
          directorForm.resetFields();
        }}
        onOk={() => directorForm.submit()}
        confirmLoading={createDirectorMutation.isPending}
        okText="Dodaj"
        cancelText="Anuluj"
      >
        <Form
          form={directorForm}
          layout="vertical"
          onFinish={(values) => createDirectorMutation.mutate(values)}
        >
          <Form.Item
            name="name"
            label="Imię i nazwisko"
            rules={[
              { required: true, message: "Imię i nazwisko jest wymagane" },
            ]}
          >
            <Input placeholder="np. Christopher Nolan" />
          </Form.Item>
          <Form.Item name="nationality_id" label="Narodowość">
            <Select
              placeholder="Wybierz narodowość"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {nationalities?.items.map((nat) => (
                <Select.Option key={nat.id} value={nat.id}>
                  {nat.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default MovieFormPage;
